// src/Dashboard/pages/user/Inscription/components/DocumentsDrawer.jsx

import React, { useEffect, useState } from "react";
import {
  FaFileMedical,
  FaFilePdf,
  FaSave,
  FaTrashAlt,
  FaUpload,
} from "react-icons/fa";
import { toast } from "react-toastify";

import DrawerShell from "./DrawerShell";

import {
  deleteDossierFile,
  updateDossierInscription,
  uploadCertificatMedical,
  uploadLicenceDefinitive,
} from "../../../../../services/dossierServices";

import {
  getLicenceInsuranceUrl,
  openInNewTab,
  toInputDate,
} from "../utils/inscriptionUtils";

function UploadBlock({
  title,
  icon,
  currentUrl,
  mode,
  setMode,
  file,
  setFile,
  url,
  setUrl,
  children,
  onSave,
  onDelete,
  saving,
}) {
  const handleModeChange = (newMode) => {
    setMode(newMode);

    if (newMode === "upload") {
      setFile(null);
      return;
    }

    setFile(null);

    if (currentUrl?.startsWith("/uploads")) {
      setUrl("");
    } else {
      setUrl(currentUrl || "");
    }
  };

  const saveDisabled =
    saving ||
    (mode === "upload" && !file) ||
    (mode === "link" && !String(url || "").trim());

  return (
    <section className="rounded-[24px] border border-slate-200 bg-white p-5 dark:border-slate-800 dark:bg-slate-950">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div className="flex items-start gap-3">
          <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl bg-slate-100 text-[#013E8E] dark:bg-slate-900 dark:text-indigo-300">
            {icon}
          </span>

          <div>
            <h3 className="font-bold text-slate-900 dark:text-white">
              {title}
            </h3>

            <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">
              {currentUrl
                ? "Document déjà fourni."
                : "Aucun document fourni."}
            </p>
          </div>
        </div>

        <div className="flex flex-wrap gap-2">
          {currentUrl && (
            <button
              type="button"
              onClick={() => openInNewTab(currentUrl)}
              className="rounded-xl bg-slate-100 px-3 py-2 text-xs font-bold text-slate-700 transition hover:bg-slate-200 dark:bg-slate-800 dark:text-slate-200 dark:hover:bg-slate-700"
            >
              Ouvrir
            </button>
          )}

          {currentUrl && (
            <button
              type="button"
              onClick={onDelete}
              disabled={saving}
              className="inline-flex items-center gap-2 rounded-xl bg-rose-600 px-3 py-2 text-xs font-bold text-white transition hover:bg-rose-700 disabled:opacity-60"
            >
              <FaTrashAlt />
              Supprimer
            </button>
          )}
        </div>
      </div>

      <div className="mt-5 space-y-4">
        <div className="flex flex-wrap gap-4 text-sm text-slate-700 dark:text-slate-200">
          <label className="inline-flex cursor-pointer items-center gap-2">
            <input
              type="radio"
              checked={mode === "upload"}
              onChange={() => handleModeChange("upload")}
              disabled={saving}
            />

            Téléverser un fichier
          </label>

          <label className="inline-flex cursor-pointer items-center gap-2">
            <input
              type="radio"
              checked={mode === "link"}
              onChange={() => handleModeChange("link")}
              disabled={saving}
            />

            Coller un lien
          </label>
        </div>

        {mode === "upload" ? (
          <input
            type="file"
            accept="application/pdf,image/*"
            onChange={(event) =>
              setFile(event.target.files?.[0] || null)
            }
            disabled={saving}
            className="w-full rounded-2xl border border-slate-300 bg-white px-3 py-3 text-sm text-slate-900 dark:border-slate-700 dark:bg-slate-900 dark:text-white"
          />
        ) : (
          <input
            type="url"
            value={url}
            onChange={(event) => setUrl(event.target.value)}
            placeholder="https://drive.google.com/..."
            disabled={saving}
            className="w-full rounded-2xl border border-slate-300 bg-white px-3 py-3 text-sm text-slate-900 outline-none focus:ring-2 focus:ring-indigo-400 dark:border-slate-700 dark:bg-slate-900 dark:text-white"
          />
        )}

        {children}

        <div className="flex justify-end">
          <button
            type="button"
            onClick={onSave}
            disabled={saveDisabled}
            className="inline-flex items-center gap-2 rounded-2xl bg-[#013E8E] px-4 py-3 text-sm font-bold text-white transition hover:bg-indigo-800 disabled:cursor-not-allowed disabled:opacity-60"
          >
            <FaSave />
            {saving ? "Enregistrement..." : "Enregistrer"}
          </button>
        </div>
      </div>
    </section>
  );
}

export default function DocumentsDrawer({
  isOpen,
  onClose,
  adherent,
  dossier,
  refreshDossier,
}) {
  const [saving, setSaving] = useState(false);

  /* Document médical */
  const [modeCM, setModeCM] = useState("upload");
  const [fileCM, setFileCM] = useState(null);
  const [urlCM, setUrlCM] = useState("");
  const [dateCM, setDateCM] = useState("");

  /* Licence-assurance finale */
  const [modeLicence, setModeLicence] = useState("upload");
  const [fileLicence, setFileLicence] = useState(null);
  const [urlLicence, setUrlLicence] = useState("");

  const currentCM = dossier?.cm_url || "";
  const currentLicence = getLicenceInsuranceUrl(dossier);

  useEffect(() => {
    if (!isOpen) return;

    setModeCM(
      currentCM?.startsWith("/uploads")
        ? "upload"
        : currentCM
          ? "link"
          : "upload",
    );

    setFileCM(null);
    setUrlCM(currentCM || "");
    setDateCM(toInputDate(dossier?.date_cm));

    setModeLicence(
      currentLicence?.startsWith("/uploads")
        ? "upload"
        : currentLicence
          ? "link"
          : "upload",
    );

    setFileLicence(null);
    setUrlLicence(currentLicence || "");
    setSaving(false);
  }, [currentCM, currentLicence, dossier?.date_cm, isOpen]);

  const ensureDossier = () => {
    if (!dossier?.id) {
      toast.error(
        "Aucun dossier d’inscription disponible pour cet adhérent.",
      );

      return false;
    }

    return true;
  };

  /* ---------- Document médical ---------- */

  const saveCM = async () => {
    if (!ensureDossier()) return;

    const cleanUrl = urlCM.trim();

    if (modeCM === "upload" && !fileCM) {
      toast.error("Choisissez un fichier PDF ou une image.");
      return;
    }

    if (modeCM === "link" && !/^https?:\/\//i.test(cleanUrl)) {
      toast.error("Lien du document médical invalide.");
      return;
    }

    try {
      setSaving(true);

      if (modeCM === "upload") {
        await uploadCertificatMedical(
          dossier.id,
          fileCM,
          dateCM
            ? {
                date_cm: dateCM,
              }
            : undefined,
        );
      } else {
        if (currentCM.startsWith("/uploads")) {
          try {
            await deleteDossierFile(dossier.id, "cm_url");
          } catch (error) {
            console.error(
              "Impossible de supprimer l’ancien document médical :",
              error,
            );
          }
        }

        await updateDossierInscription(dossier.id, {
          cm_url: cleanUrl,
          date_cm: dateCM || null,
        });
      }

      await refreshDossier?.();

      setFileCM(null);

      toast.success("Document médical enregistré.");
    } catch (error) {
      console.error(error);
      toast.error("Erreur lors de l’enregistrement du document médical.");
    } finally {
      setSaving(false);
    }
  };

  const saveCMDate = async () => {
    if (!ensureDossier()) return;

    if (!dateCM) {
      toast.warn("Indiquez la date du document médical.");
      return;
    }

    try {
      setSaving(true);

      await updateDossierInscription(dossier.id, {
        date_cm: dateCM,
      });

      await refreshDossier?.();

      toast.success("Date du document médical enregistrée.");
    } catch (error) {
      console.error(error);
      toast.error("Erreur lors de l’enregistrement de la date.");
    } finally {
      setSaving(false);
    }
  };

  /* ---------- Licence-assurance finale ---------- */

  const saveLicence = async () => {
    if (!ensureDossier()) return;

    const cleanUrl = urlLicence.trim();

    if (modeLicence === "upload" && !fileLicence) {
      toast.error("Choisissez un fichier PDF ou une image.");
      return;
    }

    if (modeLicence === "link" && !/^https?:\/\//i.test(cleanUrl)) {
      toast.error("Lien de la licence-assurance invalide.");
      return;
    }

    try {
      setSaving(true);

      if (modeLicence === "upload") {
        await uploadLicenceDefinitive(dossier.id, fileLicence);
      } else {
        if (currentLicence.startsWith("/uploads")) {
          try {
            await deleteDossierFile(dossier.id, "licence_url");
          } catch (error) {
            console.error(
              "Impossible de supprimer l’ancienne licence-assurance :",
              error,
            );
          }
        }

        await updateDossierInscription(dossier.id, {
          licence_url: cleanUrl,
          licence_assurance_url: cleanUrl,
          licence_assurance: cleanUrl,
        });
      }

      await refreshDossier?.();

      setFileLicence(null);

      toast.success("Licence-assurance finale enregistrée.");
    } catch (error) {
      console.error(error);
      toast.error(
        "Erreur lors de l’enregistrement de la licence-assurance.",
      );
    } finally {
      setSaving(false);
    }
  };

  /* ---------- Suppression ---------- */

  const removeField = async (field, label) => {
    if (!ensureDossier()) return;

    if (!window.confirm(`Supprimer « ${label} » ?`)) {
      return;
    }

    try {
      setSaving(true);

      await deleteDossierFile(dossier.id, field);
      await refreshDossier?.();

      if (field === "cm_url") {
        setFileCM(null);
        setUrlCM("");
      }

      if (field === "licence_url") {
        setFileLicence(null);
        setUrlLicence("");
      }

      toast.success(`${label} supprimé.`);
    } catch (error) {
      console.error(error);
      toast.error(`Impossible de supprimer « ${label} ».`);
    } finally {
      setSaving(false);
    }
  };

  return (
    <DrawerShell
      isOpen={isOpen}
      onClose={onClose}
      title="Documents d’inscription"
      subtitle={
        adherent
          ? `${adherent.prenom} ${adherent.nom}`
          : "Téléversement des documents"
      }
      icon={<FaUpload />}
      width="max-w-4xl"
    >
      {!dossier?.id ? (
        <div className="rounded-[24px] border border-amber-200 bg-amber-50 p-5 text-sm leading-6 text-amber-800 dark:border-amber-500/20 dark:bg-amber-500/10 dark:text-amber-200">
          Aucun dossier n’existe encore pour cet adhérent. Les documents ne
          peuvent pas être téléversés tant que le dossier n’est pas créé.
        </div>
      ) : (
        <div className="space-y-5">
          {/* Document médical */}
          <UploadBlock
            title="Certificat / Attestation médicale"
            icon={<FaFileMedical />}
            currentUrl={currentCM}
            mode={modeCM}
            setMode={setModeCM}
            file={fileCM}
            setFile={setFileCM}
            url={urlCM}
            setUrl={setUrlCM}
            onSave={saveCM}
            onDelete={() =>
              removeField("cm_url", "Document médical")
            }
            saving={saving}
          >
            <div className="grid grid-cols-1 gap-2 sm:grid-cols-[1fr_auto] sm:items-end">
              <div>
                <label className="mb-1 block text-sm font-bold text-slate-800 dark:text-slate-100">
                  Date figurant sur le document
                </label>

                <input
                  type="date"
                  value={dateCM}
                  onChange={(event) => setDateCM(event.target.value)}
                  disabled={saving}
                  className="w-full rounded-2xl border border-slate-300 bg-white px-3 py-3 text-sm text-slate-900 dark:border-slate-700 dark:bg-slate-900 dark:text-white"
                />
              </div>

              <button
                type="button"
                onClick={saveCMDate}
                disabled={saving || !dateCM}
                className="inline-flex items-center justify-center gap-2 rounded-2xl bg-emerald-600 px-4 py-3 text-sm font-bold text-white transition hover:bg-emerald-700 disabled:cursor-not-allowed disabled:opacity-60"
              >
                <FaSave />
                Enregistrer la date
              </button>
            </div>
          </UploadBlock>

          {/* Licence-assurance finale */}
          <UploadBlock
            title="Licence-assurance finale"
            icon={<FaFilePdf />}
            currentUrl={currentLicence}
            mode={modeLicence}
            setMode={setModeLicence}
            file={fileLicence}
            setFile={setFileLicence}
            url={urlLicence}
            setUrl={setUrlLicence}
            onSave={saveLicence}
            onDelete={() =>
              removeField(
                "licence_url",
                "Licence-assurance finale",
              )
            }
            saving={saving}
          >
            <p className="text-sm leading-6 text-slate-500 dark:text-slate-400">
              Ajoutez uniquement le document définitif de licence-assurance.
              Aucun formulaire de licence à signer n’est demandé.
            </p>
          </UploadBlock>
        </div>
      )}
    </DrawerShell>
  );
}